import { useEffect, useState } from 'react';
import { usePostHog } from '@posthog/react';
import type { ErrorComponentProps } from '@tanstack/react-router';
import { ErrorState } from '../components/ErrorState';
import { isModuleLinkFailure, recoverFromModuleFailure } from './moduleRecovery';

/**
 * The router's error boundary for every route.
 *
 * Without it TanStack Router falls back to its own default, which shows a bare
 * "Something went wrong!" and a "Show Error" toggle - developer output, printed
 * to customers. It also handles the failure this replaced: a module that will
 * not link because the browser cached a truncated copy of a chunk. That one is
 * repairable without the customer doing anything, so we repair it.
 */
export const RouteErrorBoundary = ({ error, reset }: ErrorComponentProps) => {
  const [recovering, setRecovering] = useState(() => isModuleLinkFailure(error));
  const posthog = usePostHog();

  useEffect(() => {
    posthog?.captureException(error, { surface: 'route_error_boundary' });
  }, [error, posthog]);

  useEffect(() => {
    if (!isModuleLinkFailure(error)) return;
    let active = true;
    void recoverFromModuleFailure().then((started) => {
      // `started` false means recovery already ran in this tab and did not
      // stick, so the page is genuinely broken. Stop reloading and say so.
      if (active && !started) setRecovering(false);
    });
    return () => {
      active = false;
    };
  }, [error]);

  if (recovering) {
    return <ErrorState message="Reloading this page to pick up the latest version..." />;
  }

  return (
    <ErrorState
      message="This page failed to load. It’s us, not you."
      onRetry={reset}
      className="my-10"
    />
  );
};
